import {
  Contact,
  CreateContactInput,
  PhoneNumberInput,
  UpdateContactInput,
  omitTypename,
} from '@akin-home/amplify';
import { filterHasValue } from '@akin-home/arrays';
import {
  ContactModel,
  ContactTypes,
  PhoneNumber,
  createContactModel,
  createPhoneNumber,
} from '@akin-home/contacts';
import { handleImageFieldsOnPost } from '@akin-home/images/frontend';
import { colors } from '@akin-home/theme';
import { deleteUserMutation } from '@akin-home/users/frontend';
import {
  createContactMutation,
  deleteContactMutation,
  updateContactMutation,
} from '../api/mutations';

import { createUserForContact } from './createUserForContact';
export * from './getCreateUserInputFromContact';

export const formatToContactModel = (item: Contact): ContactModel =>
  createContactModel({
    id: item.id,
    name: item.name,
    type: (item.type || ContactTypes.Family) as ContactTypes,
    tags: filterHasValue(item.tags),
    phoneNumbers: (item.phoneNumbers?.map(omitTypename) || [
      createPhoneNumber(),
    ]) as PhoneNumber[],
    emailAddresses: (item.emailAddresses || ['']) as string[],
    notes: filterHasValue(item.notes),
    contactColor: item.contactColor || colors.primary['90'],
    stockImage: item.stockImage || undefined,
    image: item.image || undefined,
    imageUrl: item.publicImageURL || undefined,
    habitatID: item.habitatID,
    addresses: filterHasValue(item.addresses),
    user_id: item.user_id || undefined,
  });

export const contactModelToUpdateMutationInput = (
  contact: ContactModel
): UpdateContactInput => {
  return {
    ...contactModelToCreateMutationInput(contact),
    id: contact.id,
    user_id: contact.user_id,
  };
};

export const contactModelToCreateMutationInput = (
  contact: ContactModel
): CreateContactInput => {
  return {
    habitatID: contact.habitatID,
    name: contact.name,
    type: contact.type,
    tags: contact.tags,
    phoneNumbers: contact.phoneNumbers.map(
      (phone) => phone as PhoneNumberInput
    ),
    emailAddresses: contact.emailAddresses,
    notes: contact.notes,
    contactColor: contact.contactColor || colors.primary[70],
    stockImage: contact.stockImage || '',
    imageID: contact.image?.id,
    publicImageURL: contact.imageUrl,
    addresses: contact.addresses?.map((item) => omitTypename(item) ?? null),
  };
};

export interface CreateUserRequiredDetails {
  sub: string;
  username: string;
}

export const createContactFromModel = async (params: {
  contact: ContactModel;
  create_linked_user?: CreateUserRequiredDetails;
}) => {
  const { contact, create_linked_user } = params;
  const input = contactModelToCreateMutationInput(
    await handleImageFieldsOnPost(contact)
  );

  const result = await createContactMutation({ input });

  if (!result.data.createContact) return result;

  /** Create linked user */
  if (create_linked_user) {
    /** First we format the create contact response to a contact model */
    const new_contact = formatToContactModel(
      result.data.createContact as Contact
    );

    /** Create the user */
    const {
      data: { createUser },
    } = await createUserForContact(new_contact, create_linked_user);

    if (createUser) {
      /** If successful, update the contact with the new user id */
      const {
        data: { updateContact },
      } = await updateContactMutation({
        input: {
          id: result.data.createContact?.id as string,
          user_id: createUser.id,
        },
      });

      /** Add this user id data to the returned create response */
      if (updateContact) {
        result.data.createContact.user_id = updateContact.id;
      }
    }
  }

  return result;
};

export const updateContactFromModel = async (params: {
  contact: ContactModel;
  create_linked_user?: CreateUserRequiredDetails;
  delete_linked_user?: boolean;
}) => {
  const { contact, create_linked_user, delete_linked_user } = params;
  const contact_with_image = await handleImageFieldsOnPost(contact);
  const input = contactModelToUpdateMutationInput(contact_with_image);

  if (create_linked_user) {
    /** Create the linked user */
    const {
      data: { createUser },
    } = await createUserForContact(contact_with_image, create_linked_user);

    /** Add the user id to the update input */
    if (createUser) {
      input.user_id = createUser.id;
    }
  }

  /** Check if we want to delete the linked user */
  if (delete_linked_user && contact.user_id) {
    await deleteUserMutation({ input: { id: contact.user_id } });

    /** Unset the user_id */
    input.user_id = null;
  }

  return await updateContactMutation({ input });
};

/**
 *
 * @param id The contact ID to delete
 * @param user_id The linked user ID to delete
 * @returns
 */
export const deleteContact = async (id: string, user_id?: string) => {
  const result = await deleteContactMutation({ input: { id } });

  if (user_id) {
    await deleteUserMutation({ input: { id: user_id } });
  }

  return result;
};
